import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ArcheSidebar } from './ArcheSidebar';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { SearchResultsProvider } from '../context/SearchResultsContext';

/**
 * Display mode for the ArcheLayout:
 * - 'normal': Full header + sidebar + content (for authenticated users on non-fullscreen arches)
 * - 'public': Minimal header + content, no sidebar (for unauthenticated users on public arches)
 * - 'fullscreen': Minimal header (logo + theme toggle) + content, no sidebar (for full-screen arches)
 * - 'embed': No header at all, just content (for iframe embedding on external sites)
 */
export type ArcheDisplayMode = 'normal' | 'public' | 'fullscreen' | 'embed';

interface ArcheLayoutProps {
  children: ReactNode;
  /**
   * The display mode for the layout.
   * - 'normal': Full header + sidebar + content
   * - 'public': Minimal header + content (no sidebar)
   * - 'fullscreen': Minimal header (logo + theme toggle) + content (no sidebar)
   * - 'embed': No header at all, just content (for iframe embedding)
   */
  displayMode?: ArcheDisplayMode;
}

function ArcheLayoutInner({ children, displayMode = 'normal' }: ArcheLayoutProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();

  // Embed mode: no header at all, just content (for iframe embedding)
  if (displayMode === 'embed') {
    return (
      <div className="h-screen overflow-auto">
        {children}
      </div>
    );
  }

  // Full-screen mode: minimal header (logo + theme toggle), no sidebar
  if (displayMode === 'fullscreen') {
    return (
      <div className="flex flex-col h-screen">
        <Header minimal />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    );
  }

  // Public mode (not logged in): minimal header, no sidebar
  if (displayMode === 'public') {
    return (
      <div className="flex flex-col h-screen">
        <Header minimal />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    );
  }

  // Normal mode: full header + sidebar
  return (
    <div className="flex flex-col h-screen">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar - only render on desktop */}
        {!isMobile && (
          <aside
            className={`
              flex-shrink-0 border-r border-border bg-card overflow-hidden
              transition-all duration-300 ease-in-out
              ${isCollapsed ? 'w-0' : 'w-64'}
            `}
          >
            <div className="w-64 h-full flex flex-col">
              <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
            </div>
          </aside>
        )}

        {/* Mobile Sheet Sidebar - only render on mobile */}
        {isMobile && (
          <Sheet open={!isCollapsed} onOpenChange={(open) => setCollapsed(!open)}>
            <SheetContent side="left" className="w-[280px] p-0 [&>button]:hidden">
              <div className="h-full flex flex-col">
                <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

export function ArcheLayout({ children, displayMode = 'normal' }: ArcheLayoutProps) {
  return (
    <SidebarProvider>
      <SearchResultsProvider>
        <ArcheLayoutInner displayMode={displayMode}>{children}</ArcheLayoutInner>
      </SearchResultsProvider>
    </SidebarProvider>
  );
}

export default ArcheLayout;
